import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { AlertCircle, CreditCard, Users } from 'lucide-react';
import { projectsApi, notesApi, employeesApi } from '../../lib/api';
import { PROJECT_STATUSES, formatCurrency } from '../../lib/utils';

const STATUS_LABELS = {
  'Under_Contract': 'Under Contract (Buy)',
  'Owned': 'Owned',
  'Subdividing': 'Subdividing',
  'Listed': 'Listed',
  'Under_Contract_Sell': 'Under Contract (Sell)',
  'Sold': 'Sold',
  'Note_Held': 'Note Held',
  'Dead': 'Dead Deal',
};

export const ProjectFormDialog = ({ open, onOpenChange, project, onSuccess }) => {
  const navigate = useNavigate();
  const isEditing = !!project;
  
  const [formData, setFormData] = useState({
    name: '',
    status: 'Under_Contract',
    strategy: 'Flip',
    purchase_price: '',
    funding_source: 'Land_Finder',
    is_jv_project: false,
    sale_price: '',
    contract_date: new Date().toISOString().split('T')[0],
    acquisition_date: '',
    sale_date: '',
    estimated_close_date: '',
    acres: '',
    county: '',
    state: '',
    terranotes_id: '',
    projected_profit: '',
    notes: '',
    commission_eligible_employee_ids: [],
    legal_description: '',
    apn: '',
    number_of_lots: '',
    estimated_sale_price: '',
  });
  const [loading, setLoading] = useState(false);
  const [showJvPrompt, setShowJvPrompt] = useState(false);
  const [showNoteHeldPrompt, setShowNoteHeldPrompt] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [previousStatus, setPreviousStatus] = useState(null);
  const [employees, setEmployees] = useState([]);

  // Load active employees with commission rates
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const response = await employeesApi.getAll(true);
        // Filter to only employees with commission rates
        const commissionEmployees = response.data.filter(
          emp => emp.commission_tier_1_rate && emp.commission_tier_1_rate > 0
        );
        setEmployees(commissionEmployees);
      } catch (error) {
        console.error('Failed to load employees:', error);
      }
    };
    if (open) {
      loadEmployees();
    }
  }, [open]);

  useEffect(() => {
    if (project) {
      setPreviousStatus(project.status);
      setFormData({
        name: project.name || '',
        status: project.status || 'Under_Contract',
        strategy: project.strategy || 'Flip',
        purchase_price: project.purchase_price || '',
        funding_source: project.funding_source || 'Land_Finder',
        is_jv_project: project.is_jv_project || false,
        sale_price: project.sale_price || '',
        contract_date: project.contract_date?.split('T')[0] || '',
        acquisition_date: project.acquisition_date?.split('T')[0] || '',
        sale_date: project.sale_date?.split('T')[0] || '',
        estimated_close_date: project.estimated_close_date?.split('T')[0] || '',
        acres: project.acres || '',
        county: project.county || '',
        state: project.state || '',
        terranotes_id: project.terranotes_id || '',
        projected_profit: project.projected_profit || '',
        notes: project.notes || '',
        commission_eligible_employee_ids: project.commission_eligible_employee_ids || [],
        legal_description: project.legal_description || '',
        apn: project.apn || '',
        number_of_lots: project.number_of_lots || '',
        estimated_sale_price: project.estimated_sale_price || '',
      });
    } else {
      setPreviousStatus(null);
      setFormData({
        name: '',
        status: 'Under_Contract',
        strategy: 'Flip',
        purchase_price: '',
        funding_source: 'Land_Finder',
        is_jv_project: false,
        sale_price: '',
        contract_date: new Date().toISOString().split('T')[0],
        acquisition_date: '',
        sale_date: '',
        estimated_close_date: '',
        acres: '',
        county: '',
        state: '',
        terranotes_id: '',
        projected_profit: '',
        notes: '',
        commission_eligible_employee_ids: [],
        legal_description: '',
        apn: '',
        number_of_lots: '',
        estimated_sale_price: '',
      });
      setShowJvPrompt(false);
      setShowNoteHeldPrompt(false);
      setCreatedProjectId(null);
    }
  }, [project, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        acres: formData.acres ? parseFloat(formData.acres) : null,
        projected_profit: formData.projected_profit ? parseFloat(formData.projected_profit) : null,
        contract_date: formData.contract_date || null,
        acquisition_date: formData.acquisition_date || null,
        sale_date: formData.sale_date || null,
        estimated_close_date: formData.estimated_close_date || null,
        terranotes_id: formData.terranotes_id || null,
        notes: formData.notes || null,
        is_jv_project: formData.is_jv_project,
        funding_source: formData.is_jv_project ? 'Investor' : formData.funding_source,
        commission_eligible_employee_ids: formData.commission_eligible_employee_ids || [],
        legal_description: formData.legal_description || null,
        apn: formData.apn || null,
        number_of_lots: formData.number_of_lots ? parseInt(formData.number_of_lots) : null,
        estimated_sale_price: formData.estimated_sale_price ? parseFloat(formData.estimated_sale_price) : null,
      };

      let response;
      if (isEditing) {
        response = await projectsApi.update(project.id, payload);
        
        // Check if status changed TO Note_Held
        if (formData.status === 'Note_Held' && previousStatus !== 'Note_Held') {
          setCreatedProjectId(project.id);
          setShowNoteHeldPrompt(true);
          return; // Don't close dialog yet
        }
        
        onSuccess(response.data);
      } else {
        response = await projectsApi.create(payload);
        
        // If JV project, show JV prompt
        if (formData.is_jv_project) {
          setCreatedProjectId(response.data.id);
          setShowJvPrompt(true);
        } else {
          onSuccess(response.data);
        }
      }
    } catch (error) {
      let errorMsg = error?.response?.data?.detail || 'Failed to save project';
      // Handle Pydantic validation errors (array of objects)
      if (Array.isArray(errorMsg)) {
        errorMsg = errorMsg.map(e => e.msg || String(e)).join(', ');
      } else if (typeof errorMsg === 'object') {
        errorMsg = errorMsg.msg || JSON.stringify(errorMsg);
      }
      const { toast } = await import('sonner');
      toast.error(errorMsg);
      console.error('Failed to save project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJvPromptClose = (addJv) => {
    setShowJvPrompt(false);
    // Pass project data with flag to open JV form
    if (createdProjectId) {
      onSuccess({ id: createdProjectId, openJvForm: addJv });
    }
  };

  const handleNoteHeldPromptClose = (createNote) => {
    setShowNoteHeldPrompt(false);
    
    // Use project.id as fallback for editing existing projects
    const projectId = createdProjectId || project?.id;
    
    // Pass the flag to trigger a reload instead of setting partial data
    // This tells the parent to reload the full project data
    onSuccess({ id: projectId, reload: true });
    
    if (createNote && projectId) {
      // Navigate to notes page with a small delay to ensure dialog closes properly
      const salePrice = formData.sale_price || '';
      const projectName = encodeURIComponent(formData.name || '');
      setTimeout(() => {
        navigate(`/notes?create=true&project_id=${projectId}&principal=${salePrice}&project_name=${projectName}`);
      }, 100);
    }
  };

  // Status-based field rules
  const status = formData.status;
  
  // Sale fields only visible in sell-related statuses
  const showSalePrice = isEditing && ['Under_Contract_Sell', 'Sold', 'Note_Held'].includes(status);
  const showSaleDate = isEditing && ['Sold', 'Note_Held'].includes(status);
  const requireSalePrice = ['Sold', 'Note_Held', 'Under_Contract_Sell'].includes(status);
  const requireSaleDate = ['Sold', 'Note_Held'].includes(status);
  
  // Acquisition date only visible once project progresses past Under Contract
  const showAcquisitionDate = isEditing && ['Owned', 'Subdividing', 'Listed', 'Under_Contract_Sell', 'Sold', 'Note_Held'].includes(status);
  const requireAcquisitionDate = isEditing && ['Owned', 'Subdividing', 'Listed', 'Under_Contract_Sell', 'Sold', 'Note_Held'].includes(status);
  
  // Estimated close date for active contract/listing statuses
  const showEstimatedCloseDate = ['Under_Contract', 'Under_Contract_Sell', 'Listed'].includes(status);
  const requireEstimatedCloseDate = ['Under_Contract', 'Under_Contract_Sell'].includes(status);
  
  // Contract date always visible but required for Under_Contract
  const requireContractDate = status === 'Under_Contract' || !isEditing;
  
  // Check if this is a child lot (subdivision child)
  const isChildLot = project?.parent_project_id;
  // Fields that should be read-only for child lots
  const inheritedFieldsReadOnly = isEditing && isChildLot;

  // Note Held Prompt Dialog
  if (showNoteHeldPrompt) {
    return (
      <Dialog open={true} onOpenChange={() => handleNoteHeldPromptClose(false)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-orange-500" />
              Create Note Receivable?
            </DialogTitle>
            <DialogDescription>
              This project is now owner-financed. Would you like to create a Note Receivable to track the buyer&apos;s payments?
              {formData.sale_price && (
                <span className="block mt-2 font-medium text-foreground">
                  Sale Price: {formatCurrency(parseFloat(formData.sale_price))}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => handleNoteHeldPromptClose(false)}
              className="rounded-sm"
            >
              Skip for Now
            </Button>
            <Button
              onClick={() => handleNoteHeldPromptClose(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
            >
              Create Note
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // JV Prompt Dialog
  if (showJvPrompt) {
    return (
      <Dialog open={true} onOpenChange={() => handleJvPromptClose(false)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Add JV Agreement?
            </DialogTitle>
            <DialogDescription>
              This project is investor-funded. Would you like to add a JV agreement now to set up the investor details and profit split structure?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => handleJvPromptClose(false)}
              className="rounded-sm"
            >
              Skip for Now
            </Button>
            <Button
              onClick={() => handleJvPromptClose(true)}
              className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
            >
              Add JV Agreement
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEditing ? 'Edit Project' : 'New Deal Under Contract'}
          </DialogTitle>
          {!isEditing && (
            <DialogDescription>
              Create a new project when a deal goes under contract.
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Project Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-sm"
                placeholder="e.g., Smith Ranch 40ac"
                data-testid="project-name-input"
              />
            </div>

            {isEditing && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="project-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_STATUSES.map(status => (
                      <SelectItem key={status} value={status}>
                        {STATUS_LABELS[status] || status.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Strategy {inheritedFieldsReadOnly && <span className="text-xs text-amber-600 ml-1">(inherited)</span>}</Label>
              <Select
                value={formData.strategy}
                onValueChange={(value) => setFormData({ ...formData, strategy: value })}
                disabled={inheritedFieldsReadOnly}
              >
                <SelectTrigger className={`rounded-sm ${inheritedFieldsReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} data-testid="project-strategy-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Flip">Flip</SelectItem>
                  <SelectItem value="Subdivide">Subdivide</SelectItem>
                </SelectContent>
              </Select>
              {inheritedFieldsReadOnly && (
                <p className="text-xs text-amber-600">Strategy is inherited from parent project</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Purchase Price ($) {inheritedFieldsReadOnly && <span className="text-xs text-amber-600 ml-1">(allocated)</span>}</Label>
              <Input
                type="text"
                value={formData.purchase_price ? Number(formData.purchase_price).toLocaleString('en-US') : ''}
                onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value.replace(/[^0-9.]/g, '') })}
                className={`rounded-sm ${inheritedFieldsReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                placeholder="0"
                disabled={inheritedFieldsReadOnly}
                data-testid="project-purchase-price-input"
              />
              {inheritedFieldsReadOnly ? (
                <p className="text-xs text-amber-600">
                  This is {project?.cost_basis_percentage || 0}% of parent&apos;s cost basis. Edit allocation from parent project.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  For cost basis tracking, add the purchase as a transaction when funds are sent.
                </p>
              )}
            </div>

            {showSalePrice && (
              <div className="space-y-2">
                <Label>Sale Price ($) {requireSalePrice && '*'}</Label>
                <Input
                  type="text"
                  value={formData.sale_price ? Number(formData.sale_price).toLocaleString('en-US') : ''}
                  onChange={(e) => setFormData({ ...formData, sale_price: e.target.value.replace(/[^0-9.]/g, '') })}
                  className="rounded-sm"
                  placeholder="Enter sale price"
                  required={requireSalePrice}
                  data-testid="project-sale-price-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Estimated Sale Price ($)</Label>
              <Input
                type="text"
                value={formData.estimated_sale_price ? Number(formData.estimated_sale_price).toLocaleString('en-US') : ''}
                onChange={(e) => setFormData({ ...formData, estimated_sale_price: e.target.value.replace(/[^0-9.]/g, '') })}
                className="rounded-sm"
                placeholder="Projected sale price"
                data-testid="project-estimated-sale-price-input"
              />
              <p className="text-xs text-muted-foreground">Shows on project card until actual sale price is entered</p>
            </div>

            <div className="space-y-2">
              <Label>Contract Date {requireContractDate && '*'} {inheritedFieldsReadOnly && <span className="text-xs text-amber-600 ml-1">(inherited)</span>}</Label>
              <Input
                type="date"
                value={formData.contract_date}
                onChange={(e) => setFormData({ ...formData, contract_date: e.target.value })}
                className={`rounded-sm ${inheritedFieldsReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                required={requireContractDate}
                disabled={inheritedFieldsReadOnly}
                data-testid="project-contract-date-input"
              />
              {inheritedFieldsReadOnly ? (
                <p className="text-xs text-amber-600">Contract date is inherited from parent project</p>
              ) : (
                <p className="text-xs text-muted-foreground">Date the deal went under contract</p>
              )}
            </div>

            {showAcquisitionDate && (
              <div className="space-y-2">
                <Label>Acquisition Date {requireAcquisitionDate && '*'} {inheritedFieldsReadOnly && <span className="text-xs text-amber-600 ml-1">(inherited)</span>}</Label>
                <Input
                  type="date"
                  value={formData.acquisition_date}
                  onChange={(e) => setFormData({ ...formData, acquisition_date: e.target.value })}
                  className={`rounded-sm ${inheritedFieldsReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  disabled={inheritedFieldsReadOnly}
                  required={requireAcquisitionDate}
                  data-testid="project-acq-date-input"
                />
                {inheritedFieldsReadOnly ? (
                  <p className="text-xs text-amber-600">Acquisition date is inherited from parent project</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Date the property was actually purchased</p>
                )}
              </div>
            )}

            {showSaleDate && (
              <div className="space-y-2">
                <Label>Sale Date {requireSaleDate && '*'}</Label>
                <Input
                  type="date"
                  value={formData.sale_date}
                  onChange={(e) => setFormData({ ...formData, sale_date: e.target.value })}
                  className="rounded-sm"
                  required={requireSaleDate}
                  data-testid="project-sale-date-input"
                />
              </div>
            )}

            {showEstimatedCloseDate && (
              <div className="space-y-2">
                <Label>Estimated Close Date {requireEstimatedCloseDate && '*'}</Label>
                <Input
                  type="date"
                  value={formData.estimated_close_date}
                  onChange={(e) => setFormData({ ...formData, estimated_close_date: e.target.value })}
                  className="rounded-sm"
                  required={requireEstimatedCloseDate}
                  data-testid="project-estimated-close-date-input"
                />
                <p className="text-xs text-muted-foreground">Expected closing date — used for capital forecasting</p>
              </div>
            )}

            {!isEditing && (
              <div className="space-y-2">
                <Label>Projected Profit ($)</Label>
                <Input
                  type="number"
                  value={formData.projected_profit}
                  onChange={(e) => setFormData({ ...formData, projected_profit: e.target.value })}
                  className="rounded-sm"
                  placeholder="Estimated profit"
                  data-testid="project-projected-profit-input"
                />
              </div>
            )}

            {/* JV Project Checkbox */}
            {!isEditing && (
              <div className="col-span-2 flex items-center space-x-2 p-3 bg-muted/30 rounded-sm border">
                <Checkbox
                  id="is_jv_project"
                  checked={formData.is_jv_project}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_jv_project: checked })}
                  data-testid="project-jv-checkbox"
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="is_jv_project"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    This is a Joint Venture (JV) Project
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Check this if investors are contributing capital to this deal
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Acres</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.acres}
                onChange={(e) => setFormData({ ...formData, acres: e.target.value })}
                className="rounded-sm"
                placeholder="0"
                data-testid="project-acres-input"
              />
            </div>

            <div className="space-y-2">
              <Label>County</Label>
              <Input
                value={formData.county}
                onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                className="rounded-sm"
                placeholder="e.g., Travis"
                data-testid="project-county-input"
              />
            </div>

            <div className="space-y-2">
              <Label>State</Label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="rounded-sm"
                placeholder="e.g., TX"
                data-testid="project-state-input"
              />
            </div>

            {/* Contract Fields */}
            <div className="space-y-2">
              <Label>APN (Parcel Number)</Label>
              <Input
                value={formData.apn}
                onChange={(e) => setFormData({ ...formData, apn: e.target.value })}
                className="rounded-sm"
                placeholder="e.g., 123-456-789"
                data-testid="project-apn-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Number of Lots</Label>
              <Input
                type="number"
                value={formData.number_of_lots}
                onChange={(e) => setFormData({ ...formData, number_of_lots: e.target.value })}
                className="rounded-sm"
                placeholder="0"
                data-testid="project-number-of-lots-input"
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Legal Description</Label>
              <Textarea
                value={formData.legal_description}
                onChange={(e) => setFormData({ ...formData, legal_description: e.target.value })}
                className="rounded-sm"
                placeholder="Property legal description for JV contracts..."
                rows={2}
                data-testid="project-legal-description-input"
              />
            </div>

            {/* Commission-Eligible Employees - owner only, not for child lots */}
            {!inheritedFieldsReadOnly && employees.length > 0 && (
              <div className="col-span-2 space-y-2 p-3 bg-muted/30 rounded-sm border">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <Label className="font-medium">Commission-Eligible Employees</Label>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Select employees who will receive commission on this deal. If none selected, no commission is required before profit allocation.
                </p>
                <div className="space-y-2">
                  {employees.map((employee) => (
                    <div key={employee.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`emp-${employee.id}`}
                        checked={formData.commission_eligible_employee_ids.includes(employee.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({
                              ...formData,
                              commission_eligible_employee_ids: [
                                ...formData.commission_eligible_employee_ids,
                                employee.id
                              ]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              commission_eligible_employee_ids: formData.commission_eligible_employee_ids.filter(
                                (id) => id !== employee.id
                              )
                            });
                          }
                        }}
                        data-testid={`commission-employee-${employee.id}`}
                      />
                      <label
                        htmlFor={`emp-${employee.id}`}
                        className="text-sm cursor-pointer flex items-center gap-2"
                      >
                        <span>{employee.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(employee.commission_tier_1_rate * 100).toFixed(0)}% T1 rate)
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
                {formData.commission_eligible_employee_ids.length > 0 && (
                  <p className="text-xs text-amber-600 mt-2">
                    ⚠️ Commission must be recorded for selected employees before profit can be allocated.
                  </p>
                )}
              </div>
            )}

            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-sm"
                placeholder="Additional notes..."
                rows={2}
                data-testid="project-notes-input"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold"
              data-testid="submit-project-btn"
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Project' : 'Create Project')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
